import { useState, useCallback, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { formatDate as formatDateShared } from "@/lib/dateFormat";
import {
  MessageSquare,
  Mail,
  MessageCircle,
  CheckCircle,
  XCircle,
  Plus,
  Loader2,
  RefreshCw,
  MoreHorizontal,
  Trash2,
  Power,
  Webhook,
  Copy,
  Star,
  Pencil,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchInput } from "@/components/ui/search-input";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Pagination } from "@/components/ui/pagination";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePermissions } from "@/hooks/usePermissions";
import {
  platformApi,
  type ChannelConfig,
  type MessagingHealthResponse,
} from "@/services/api/platform";
import {
  usePaginatedData,
  type PaginatedResponse,
} from "@/hooks/usePaginatedData";

export const Route = createFileRoute("/portal/messaging/")({
  component: MessagingChannelsPage,
});

const CHANNEL_TYPES = [
  { value: "teams", label: "Microsoft Teams", icon: MessageSquare },
  { value: "email", label: "Email", icon: Mail },
  { value: "whatsapp", label: "WhatsApp", icon: MessageCircle },
] as const;

const CHANNEL_CONFIG_FIELDS: Record<
  string,
  { key: string; label: string; placeholder: string; type?: string; optional?: boolean }[]
> = {
  teams: [
    { key: "webhook_url", label: "Webhook URL", placeholder: "https://outlook.office.com/webhook/..." },
  ],
  email: [
    {
      key: "from_email",
      label: "From address (optional)",
      placeholder: "billing@yourcompany.com",
      optional: true,
    },
    {
      key: "reply_to",
      label: "Reply-to address (optional)",
      placeholder: "accounts@yourcompany.com",
      optional: true,
    },
  ],
  whatsapp: [
    { key: "phone_number_id", label: "Phone Number ID", placeholder: "Meta phone number ID" },
    { key: "business_account_id", label: "WhatsApp Business Account ID (WABA ID)", placeholder: "Meta WABA ID — required to sync templates" },
    { key: "access_token", label: "Access Token", placeholder: "Meta API access token", type: "password" },
    { key: "app_secret", label: "App Secret", placeholder: "Meta App Secret (App Dashboard → Settings → Basic)", type: "password" },
  ],
};

const CHANNEL_HINTS: Partial<Record<string, string>> = {
  email:
    "Leave From blank to send from notifications@larasoft.global. To send from your own address (e.g. billing@larasoft.global), enter it here — it must be a verified larasoft.global address (others fall back to the default). Replies route to the Reply-to address if set. Sent via our managed SendGrid service.",
  whatsapp: "After saving, we'll generate a webhook callback URL and verify token for you. You'll paste those into your Meta webhook configuration in the next step — no need to invent a random string yourself.",
};

function getChannelIcon(type: string) {
  switch (type) {
    case "email":
      return Mail;
    case "whatsapp":
      return MessageCircle;
    case "teams":
      return MessageSquare;
    default:
      return MessageSquare;
  }
}

function MessagingChannelsPage() {
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const { can } = usePermissions();
  const organisationId = selectedOrganisation?.organisationId;
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const canCreate = can("create", "messaging");
  const canEdit = can("edit", "messaging");
  const canDelete = can("delete", "messaging");

  // Dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingChannel, setEditingChannel] = useState<ChannelConfig | null>(null);
  const [deletingChannel, setDeletingChannel] = useState<ChannelConfig | null>(null);
  const [webhookChannel, setWebhookChannel] = useState<ChannelConfig | null>(null);
  // verify_token is redacted in the channel config, so fetch the real value on
  // demand when the Webhook Settings dialog opens.
  const [webhookVerifyToken, setWebhookVerifyToken] = useState("");
  const [phoneRegistering, setPhoneRegistering] = useState(false);
  const [phoneStatusLoading, setPhoneStatusLoading] = useState(false);
  const [livePhoneStatus, setLivePhoneStatus] = useState<
    import("@/services/api/platform").WhatsAppPhoneStatus | null
  >(null);

  // Form state
  const [formChannelType, setFormChannelType] = useState("teams");
  const [formName, setFormName] = useState("");
  const [formConfig, setFormConfig] = useState<Record<string, string>>({});
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  // Service health (fetched independently)
  const [health, setHealth] = useState<MessagingHealthResponse | null>(null);
  const [healthLoading, setHealthLoading] = useState(true);

  const fetchHealth = async () => {
    if (!token) return;
    setHealthLoading(true);
    try {
      const data = await platformApi.getMessagingHealth(token);
      setHealth(data);
    } catch {
      setHealth(null);
    } finally {
      setHealthLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Server-side paginated data fetch
  const fetchChannelConfigs = useCallback(
    async (
      page: number,
      pageSize: number,
    ): Promise<PaginatedResponse<ChannelConfig>> => {
      if (!token || !organisationId) {
        return { data: [], totalCount: 0, page, pageSize };
      }
      // ME28: do NOT swallow fetch failures into an empty list. During an
      // outage that made the operator see "no channels configured yet" and
      // re-create the channel — regenerating the verify_token and breaking the
      // live Meta webhook handshake. Let the error propagate so the shared
      // error state + retry (see "Failed to load channels" below) renders.
      const response = await platformApi.listChannelConfigs(token, organisationId, {
        offset: (page - 1) * pageSize,
        limit: pageSize,
        search: searchTerm || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      return {
        data: response.items || [],
        totalCount: response.total || 0,
        page,
        pageSize,
      };
    },
    [token, organisationId, searchTerm, sortBy, sortOrder],
  );

  const {
    data: channels,
    isLoading,
    error,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
  } = usePaginatedData<ChannelConfig>({
    fetchFn: fetchChannelConfigs,
    initialPageSize: 25,
    dependencies: [token, organisationId, searchTerm, sortBy, sortOrder],
  });

  function handleSort(column: string) {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  }
  function getSortDirection(column: string): "asc" | "desc" | null {
    return sortBy === column ? sortOrder : null;
  }

  const formatDate = (dateString: string) => formatDateShared(dateString);

  // Form handlers
  const resetForm = () => {
    setFormChannelType("teams");
    setFormName("");
    setFormConfig({});
  };

  const openCreateDialog = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  const openEditDialog = (channel: ChannelConfig) => {
    setFormChannelType(channel.channel_type);
    setFormName(channel.name || "");
    // Prefill the editable config fields (e.g. email From / Reply-to) from the
    // channel's current config; hidden fields (WhatsApp tokens) are preserved
    // on submit by merging rather than replacing.
    const fields = CHANNEL_CONFIG_FIELDS[channel.channel_type] || [];
    const prefilled: Record<string, string> = {};
    for (const f of fields) {
      const v = channel.config?.[f.key];
      if (v != null) prefilled[f.key] = String(v);
    }
    setFormConfig(prefilled);
    setEditingChannel(channel);
  };

  const closeDialogs = () => {
    setShowCreateDialog(false);
    setEditingChannel(null);
    resetForm();
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !organisationId) return;

    // Optional email overrides must be well-formed addresses if provided
    // (the domain itself is authorised server-side).
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const [key, label] of [
      ["from_email", "From address"],
      ["reply_to", "Reply-to address"],
    ] as const) {
      const value = formConfig[key]?.trim();
      if (value && !emailRe.test(value)) {
        toast.error(`${label} must be a valid email address`);
        return;
      }
    }

    setFormSubmitting(true);
    try {
      if (editingChannel) {
        // Merge over the existing config so hidden fields (e.g. WhatsApp
        // verify_token / registration_status) survive an edit that only
        // changes the From / Reply-to or name.
        await platformApi.updateChannelConfig(token, editingChannel.id, {
          name: formName,
          config: { ...editingChannel.config, ...formConfig } as Record<string, string>,
        });
        toast.success("Channel updated successfully");
        closeDialogs();
        refresh();
        return;
      }

      const created = await platformApi.createChannelConfig(token, organisationId, {
        channel_type: formChannelType,
        name: formName,
        config: formConfig,
      });
      toast.success("Channel created successfully");
      closeDialogs();
      refresh();
      // WhatsApp channels have an auto-generated verify_token that the user must
      // paste into Meta's webhook config — open the webhook settings dialog so
      // they can copy it immediately.
      if (formChannelType === "whatsapp" && created) {
        setWebhookChannel(created);
      }
    } catch (err) {
      console.error("Failed to save channel:", err);
      toast.error(editingChannel ? "Failed to update channel" : "Failed to create channel");
    } finally {
      setFormSubmitting(false);
    }
  };

  const copyToClipboard = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error(`Failed to copy ${label}`);
    }
  };

  const webhookCallbackUrl = webhookChannel
    ? `https://api.larasoft.global/api/v1/gateway/messaging/webhooks/whatsapp/${webhookChannel.organisation_id}`
    : "";
  const registrationStatus =
    (webhookChannel?.config as Record<string, string> | undefined)?.registration_status;
  const registrationError =
    (webhookChannel?.config as Record<string, string> | undefined)?.registration_error;

  const handleRegisterPhone = async () => {
    if (!token || !webhookChannel) return;
    setPhoneRegistering(true);
    setLivePhoneStatus(null);
    try {
      const updated = await platformApi.registerWhatsAppPhone(token, webhookChannel.id);
      setWebhookChannel(updated);
      const ok =
        (updated.config as Record<string, string> | undefined)?.registration_status ===
        "registered";
      if (ok) {
        toast.success("Phone number registered with Meta");
      } else {
        toast.error(
          (updated.config as Record<string, string> | undefined)?.registration_error ||
            "Registration failed — see error in dialog",
        );
      }
      // Refresh the channels list so the badge stays in sync after dialog close.
      refresh();
    } catch (err) {
      console.error("Failed to register phone:", err);
      const detail =
        err instanceof Error && err.message ? err.message : "Failed to register phone";
      toast.error(detail);
    } finally {
      setPhoneRegistering(false);
    }
  };

  const handleRefreshPhoneStatus = async () => {
    if (!token || !webhookChannel) return;
    setPhoneStatusLoading(true);
    try {
      const status = await platformApi.getWhatsAppPhoneStatus(token, webhookChannel.id);
      setLivePhoneStatus(status);
    } catch (err) {
      console.error("Failed to fetch phone status:", err);
      toast.error(
        err instanceof Error && err.message
          ? err.message
          : "Failed to fetch phone status from Meta",
      );
    } finally {
      setPhoneStatusLoading(false);
    }
  };

  // Reset transient phone-status state whenever the dialog closes, and fetch
  // the (redacted) verify_token from the reveal endpoint when it opens.
  useEffect(() => {
    if (!webhookChannel) {
      setLivePhoneStatus(null);
      setPhoneStatusLoading(false);
      setPhoneRegistering(false);
      setWebhookVerifyToken("");
      return;
    }
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await platformApi.revealVerifyToken(token, webhookChannel.id);
        if (!cancelled) setWebhookVerifyToken(res.verify_token || "");
      } catch (err) {
        console.error("Failed to reveal verify token:", err);
        if (!cancelled) setWebhookVerifyToken("");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [webhookChannel, token]);

  const handleDelete = async () => {
    if (!token || !deletingChannel) return;

    setDeleteSubmitting(true);
    try {
      await platformApi.deleteChannelConfig(token, deletingChannel.id);
      toast.success("Channel deleted successfully");
      setDeletingChannel(null);
      refresh();
    } catch (err) {
      console.error("Failed to delete channel:", err);
      toast.error("Failed to delete channel");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleToggleStatus = async (channel: ChannelConfig) => {
    if (!token) return;
    const newStatus = !channel.is_active;
    try {
      await platformApi.updateChannelConfig(token, channel.id, { is_active: newStatus });
      toast.success(`Channel ${newStatus ? "activated" : "deactivated"}`);
      refresh();
    } catch (err) {
      console.error("Failed to update channel:", err);
      toast.error("Failed to update channel status");
    }
  };

  const handleSetDefault = async (channel: ChannelConfig) => {
    if (!token) return;
    try {
      await platformApi.setDefaultChannelConfig(token, channel.id);
      toast.success(`"${channel.name}" is now the default ${channel.channel_type} channel`);
      refresh();
    } catch (err) {
      console.error("Failed to set default channel:", err);
      toast.error("Failed to set default channel");
    }
  };

  const activeCount = channels.filter((c) => c.is_active).length;
  const configFields = CHANNEL_CONFIG_FIELDS[formChannelType] || [];
  const channelHint = CHANNEL_HINTS[formChannelType];

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Channels</h1>
          <p className="mt-2 text-lg text-slate-600">
            Configure messaging channels — Email, WhatsApp, Teams
          </p>
        </div>
        <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
          <div className="py-12 text-center">
            <p className="text-destructive">
              Failed to load channels: {error}
            </p>
            <Button onClick={refresh} variant="outline" className="mt-4">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900">Channels</h1>
          <p className="mt-2 text-lg text-slate-600">
            Configure messaging channels — Email, WhatsApp, Teams
          </p>
        </div>
        {canCreate && (
          <Button onClick={openCreateDialog} className="rounded-full">
            <Plus className="mr-2 h-4 w-4" />
            Add Channel
          </Button>
        )}
      </div>

      {/* Content Panel */}
      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm space-y-6">
        {/* Controls bar */}
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            placeholder="Search channels..."
            containerClassName="flex-1 min-w-[200px]"
          />
          <Button variant="outline" size="sm" onClick={() => { refresh(); fetchHealth(); }}>
            {isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Channels
              </CardTitle>
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{pagination.totalCount}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Active (this page)
              </CardTitle>
              <CheckCircle className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{activeCount}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Service Health
              </CardTitle>
              {health?.status === "healthy" ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : (
                <XCircle className="h-4 w-4 text-red-500" />
              )}
            </CardHeader>
            <CardContent>
              {healthLoading ? (
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              ) : health ? (
                <Badge
                  variant={
                    health.status === "healthy" ? "success" : "destructive"
                  }
                >
                  {health.status}
                </Badge>
              ) : (
                <Badge variant="outline">Unavailable</Badge>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : channels.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchTerm
                ? "No channels match your search"
                : "No channel configurations yet"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm
                ? "Try a different search term."
                : "Add a channel to start sending messages for this organisation."}
            </p>
            {!searchTerm && canCreate && (
              <Button onClick={openCreateDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Add Channel
              </Button>
            )}
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    direction={getSortDirection("name")}
                    onSort={() => handleSort("name")}
                  >
                    Channel
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("channel_type")}
                    onSort={() => handleSort("channel_type")}
                  >
                    Type
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("is_active")}
                    onSort={() => handleSort("is_active")}
                  >
                    Status
                  </SortableTableHead>
                  <SortableTableHead
                    direction={getSortDirection("created_at")}
                    onSort={() => handleSort("created_at")}
                  >
                    Created
                  </SortableTableHead>
                  <SortableTableHead
                    direction={null}
                    onSort={() => {}}
                    disabled
                    className="w-[50px]"
                  >
                    {" "}
                  </SortableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {channels.map((channel) => {
                  const ChannelIcon = getChannelIcon(channel.channel_type);
                  return (
                    <TableRow key={channel.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center">
                            <ChannelIcon className="h-5 w-5 text-primary" />
                          </div>
                          <div className="font-medium">{channel.name}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Badge variant="outline" className="text-xs capitalize">
                            {channel.channel_type}
                          </Badge>
                          {channel.is_default && (
                            <Badge variant="secondary" className="text-xs">
                              Default
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            channel.is_active ? "success" : "destructive"
                          }
                        >
                          {channel.is_active ? (
                            <span className="flex items-center">
                              <CheckCircle className="h-3 w-3 mr-1" /> Active
                            </span>
                          ) : (
                            <span className="flex items-center">
                              <XCircle className="h-3 w-3 mr-1" /> Inactive
                            </span>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {formatDate(channel.created_at)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {/* ME6: channel config mutations are org_admin+ only.
                                A member keeps read-only visibility (incl. Webhook
                                Settings below) but can't edit/toggle/set-default. */}
                            {canEdit && (
                              <DropdownMenuItem
                                onClick={() => openEditDialog(channel)}
                              >
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit Channel
                              </DropdownMenuItem>
                            )}
                            {canEdit && (
                              <DropdownMenuItem
                                onClick={() => handleToggleStatus(channel)}
                              >
                                <Power className="h-4 w-4 mr-2" />
                                {channel.is_active ? "Deactivate Channel" : "Activate Channel"}
                              </DropdownMenuItem>
                            )}
                            {canEdit && !channel.is_default && (
                              <DropdownMenuItem
                                onClick={() => handleSetDefault(channel)}
                              >
                                <Star className="h-4 w-4 mr-2" />
                                Set as Default
                              </DropdownMenuItem>
                            )}
                            {channel.channel_type === "whatsapp" && (
                              <DropdownMenuItem
                                onClick={() => setWebhookChannel(channel)}
                              >
                                <Webhook className="h-4 w-4 mr-2" />
                                Webhook Settings
                              </DropdownMenuItem>
                            )}
                            {canDelete && (
                              <DropdownMenuItem
                                onClick={() => setDeletingChannel(channel)}
                                className="text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Channel
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {/* Pagination */}
            {pagination.totalCount > 0 && (
              <div className="mt-4 border-t pt-4">
                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  totalCount={pagination.totalCount}
                  pageSize={pagination.pageSize}
                  pageSizeOptions={pageSizeOptions}
                  startIndex={startIndex}
                  endIndex={endIndex}
                  canGoPrev={canGoPrev}
                  canGoNext={canGoNext}
                  onPageChange={goToPage}
                  onPageSizeChange={setPageSize}
                  onFirstPage={firstPage}
                  onLastPage={lastPage}
                  onPrevPage={prevPage}
                  onNextPage={nextPage}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Create / Edit Channel Dialog */}
      <Dialog
        open={showCreateDialog || !!editingChannel}
        onOpenChange={(open) => !open && closeDialogs()}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingChannel ? "Edit Channel" : "Add Channel"}</DialogTitle>
            <DialogDescription>
              Configure a messaging channel for {selectedOrganisation?.organisationName || "this organisation"}.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleFormSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label>Channel Type</Label>
                <Select
                  value={formChannelType}
                  onValueChange={(value) => {
                    setFormChannelType(value);
                    setFormConfig({});
                  }}
                  disabled={!!editingChannel}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select channel type" />
                  </SelectTrigger>
                  <SelectContent>
                    {CHANNEL_TYPES.map((ct) => (
                      <SelectItem key={ct.value} value={ct.value}>
                        {ct.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-2">
                <Label htmlFor="channel-name">Name</Label>
                <Input
                  id="channel-name"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder={`e.g. ${formChannelType === "teams" ? "Support Notifications" : formChannelType === "email" ? "Transactional Email" : "WhatsApp Business"}`}
                  required
                />
              </div>

              {configFields.map((field) => (
                <div key={field.key} className="grid gap-2">
                  <Label htmlFor={field.key}>{field.label}</Label>
                  <Input
                    id={field.key}
                    type={field.type || "text"}
                    value={formConfig[field.key] || ""}
                    onChange={(e) =>
                      setFormConfig({ ...formConfig, [field.key]: e.target.value })
                    }
                    placeholder={field.placeholder}
                    required={!field.optional}
                  />
                </div>
              ))}

              {channelHint && (
                <p className="text-sm text-muted-foreground">{channelHint}</p>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialogs}>
                Cancel
              </Button>
              <Button type="submit" disabled={formSubmitting}>
                {formSubmitting && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {editingChannel ? "Save Changes" : "Add Channel"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Webhook Settings Dialog (WhatsApp) */}
      <Dialog
        open={!!webhookChannel}
        onOpenChange={(open) => !open && setWebhookChannel(null)}
      >
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>WhatsApp Webhook Settings</DialogTitle>
            <DialogDescription>
              Paste these EXACT values into Meta Developer Console &rarr; your WhatsApp app &rarr; Configuration &rarr; Webhook. Subscribe to the <code className="text-xs bg-muted px-1 rounded">messages</code> field.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid gap-2">
              <Label>Callback URL</Label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={webhookCallbackUrl}
                  className="font-mono text-xs"
                  onFocus={(e) => e.currentTarget.select()}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(webhookCallbackUrl, "Callback URL")}
                  aria-label="Copy callback URL"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Verify Token</Label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={webhookVerifyToken}
                  className="font-mono text-xs"
                  onFocus={(e) => e.currentTarget.select()}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(webhookVerifyToken, "Verify Token")}
                  aria-label="Copy verify token"
                  disabled={!webhookVerifyToken}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              {!webhookVerifyToken && (
                <p className="text-xs text-destructive">
                  No verify token found on this channel. Re-create the channel to auto-generate one.
                </p>
              )}
            </div>

            {/* Phone number registration + live Meta status */}
            <div className="grid gap-2 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label>Phone Number</Label>
                {registrationStatus === "registered" && (
                  <Badge variant="success" className="text-xs">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Registered with Meta
                  </Badge>
                )}
                {registrationStatus === "failed" && (
                  <Badge variant="destructive" className="text-xs">
                    <XCircle className="h-3 w-3 mr-1" />
                    Registration failed
                  </Badge>
                )}
                {!registrationStatus && (
                  <Badge variant="outline" className="text-xs">
                    Not registered
                  </Badge>
                )}
              </div>
              {registrationError && (
                <p className="text-xs text-destructive">
                  <strong>Last error:</strong> {registrationError}
                </p>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleRegisterPhone}
                  disabled={phoneRegistering}
                >
                  {phoneRegistering ? (
                    <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                  ) : null}
                  {registrationStatus === "registered"
                    ? "Re-register"
                    : registrationStatus === "failed"
                      ? "Retry registration"
                      : "Register now"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRefreshPhoneStatus}
                  disabled={phoneStatusLoading}
                >
                  {phoneStatusLoading ? (
                    <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3 w-3 mr-2" />
                  )}
                  Refresh from Meta
                </Button>
              </div>

              {/* Live Meta status (shown after Refresh) */}
              {livePhoneStatus && (
                <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs space-y-1">
                  <p className="font-semibold text-slate-700 mb-1">
                    Live from Meta:
                  </p>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                    <span className="text-slate-500">Status:</span>
                    <span className="font-mono">
                      {livePhoneStatus.status ?? "—"}
                    </span>
                    <span className="text-slate-500">Verification:</span>
                    <span className="font-mono">
                      {livePhoneStatus.code_verification_status ?? "—"}
                    </span>
                    <span className="text-slate-500">Quality:</span>
                    <span className="font-mono">
                      {livePhoneStatus.quality_rating ?? "—"}
                    </span>
                    <span className="text-slate-500">Display name:</span>
                    <span>{livePhoneStatus.verified_name ?? "—"}</span>
                    <span className="text-slate-500">Phone:</span>
                    <span className="font-mono">
                      {livePhoneStatus.display_phone_number ?? "—"}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
              <strong>Keep these secret.</strong> Anyone with the verify token can complete the webhook handshake with Meta on your behalf.
            </div>
          </div>
          <DialogFooter>
            <Button type="button" onClick={() => setWebhookChannel(null)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!deletingChannel}
        onOpenChange={(open) => !open && setDeletingChannel(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Channel?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deletingChannel?.name}&quot;? This
              will stop all messages through this channel.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSubmitting}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteSubmitting && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Delete Channel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
